import { getLocale } from '../i18n'
import { cloneSerializable } from '../utils/serialization'

type UserDataExportInput = {
   snapshot: unknown
   fastHistory: unknown
   weightHistory: unknown
   habits: unknown
   measurements: unknown
   userProfile: Record<string, unknown>
   currentFast: unknown
   lastPersistMeta: unknown
   userId: string
   stateVersion: number
   userSettings: Record<string, unknown>
}

/**
 * Returns the value when it is an array, otherwise an empty array.
 *
 * @param value - The candidate value.
 * @returns An array.
 */
function asArray(value: unknown): unknown[] {
   return Array.isArray(value) ? value : []
}

/**
 * Returns the value when it is an object, otherwise null.
 *
 * @param value - The candidate value.
 * @returns The record, or null.
 */
function asRecordOrNull(value: unknown): Record<string, unknown> | null {
   return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

/**
 * Resolves the device's IANA time zone, or null when unavailable.
 *
 * @returns The time zone, or null.
 */
function resolveDeviceTimeZone(): string | null {
   if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
      return null
   }
   try {
      const resolved = Intl.DateTimeFormat().resolvedOptions()
      return resolved && typeof resolved.timeZone === 'string' ? resolved.timeZone : null
   } catch {
      return null
   }
}

/**
 * Returns the browser user-agent string, or null when unavailable.
 *
 * @returns The user agent, or null.
 */
function resolveUserAgent(): string | null {
   return typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string' ?
         navigator.userAgent
      :  null
}

/**
 * Builds the full user-data export payload (normalized and raw state, sync metadata, device info).
 *
 * @param input - The state and profile to include in the export.
 * @returns The export payload object.
 */
export function buildUserDataExportPayload(input: UserDataExportInput) {
   const rawProfile = asRecordOrNull(cloneSerializable(input.userProfile))
   const lastPersistMeta = asRecordOrNull(input.lastPersistMeta)

   return {
      schemaVersion: 1,
      generatedAt: new Date().toISOString(),
      locale: getLocale(),
      timezoneOffsetMinutes: new Date().getTimezoneOffset(),
      userId: input.userId || null,
      stateVersion: input.stateVersion,
      normalizedState: input.snapshot,
      rawState: {
         currentFast: asRecordOrNull(cloneSerializable(input.currentFast)),
         fastHistory: asArray(cloneSerializable(input.fastHistory)),
         weightHistory: asArray(cloneSerializable(input.weightHistory)),
         habits: asArray(cloneSerializable(input.habits)),
         measurements: asArray(cloneSerializable(input.measurements)),
         userProfile: rawProfile ?? { ...input.userProfile },
         userSettings: input.userSettings,
      },
      sync: {
         lastPersist: lastPersistMeta ? cloneSerializable(lastPersistMeta) : null,
      },
      device: {
         timeZone: resolveDeviceTimeZone(),
         userAgent: resolveUserAgent(),
      },
   }
}
